import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { styled } from '@mui/material/styles';
import { Paper } from '@mui/material';

const PreviewContainer = styled(Paper)(({ theme }) => ({
  height: '100%',
  padding: '32px 36px',
  overflowY: 'auto',
  borderRadius: 0,
  boxShadow: 'none',
  border: 'none',
  backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#ffffff',
  color: theme.palette.mode === 'dark' ? '#cbd5e1' : '#1e293b',
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: '15px',
  lineHeight: 1.75,

  '& h1': {
    fontSize: '1.9em',
    fontWeight: 800,
    marginTop: '1.5rem',
    marginBottom: '0.75rem',
    paddingBottom: '0.5rem',
    borderBottom: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(14, 165, 233, 0.3)' : 'rgba(14, 165, 233, 0.2)'}`,
    lineHeight: 1.25,
    color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#0f172a',
    letterSpacing: '-0.02em',
  },
  '& h2': {
    fontSize: '1.45em',
    fontWeight: 700,
    marginTop: '1.75rem',
    marginBottom: '0.6rem',
    paddingBottom: '0.35rem',
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : '#f1f5f9'}`,
    lineHeight: 1.3,
    color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#0f172a',
    letterSpacing: '-0.01em',
  },
  '& h3': {
    fontSize: '1.2em',
    fontWeight: 700,
    marginTop: '1.5rem',
    marginBottom: '0.5rem',
    lineHeight: 1.4,
    color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#1e293b',
  },
  '& h4, & h5, & h6': {
    fontSize: '1.05em',
    fontWeight: 600,
    marginTop: '1.25rem',
    marginBottom: '0.4rem',
    lineHeight: 1.4,
    color: theme.palette.mode === 'dark' ? '#cbd5e1' : '#334155',
  },
  '& p': {
    marginBottom: '0.85rem',
    lineHeight: 1.75,
  },
  '& ul, & ol': {
    paddingLeft: '1.75em',
    marginBottom: '0.85rem',
  },
  '& li': {
    marginBottom: '0.3rem',
    lineHeight: 1.65,
  },
  '& li > ul, & li > ol': {
    marginTop: '0.25rem',
    marginBottom: 0,
  },
  '& blockquote': {
    borderLeft: `3px solid ${theme.palette.mode === 'dark' ? '#0ea5e9' : '#0ea5e9'}`,
    margin: '1.25rem 0',
    padding: '0.6rem 1.2rem',
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(14, 165, 233, 0.08)' : 'rgba(14, 165, 233, 0.05)',
    borderRadius: '0 10px 10px 0',
    color: theme.palette.mode === 'dark' ? '#94a3b8' : '#475569',
    fontStyle: 'italic',
    '& p': {
      marginBottom: '0.25rem',
    },
  },
  '& pre': {
    backgroundColor: theme.palette.mode === 'dark' ? '#0d1117' : '#0f172a',
    color: '#e2e8f0',
    padding: '1.1rem 1.25rem',
    borderRadius: '10px',
    overflowX: 'auto',
    margin: '1rem 0',
    fontSize: '0.855em',
    lineHeight: 1.65,
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.3)'}`,
    boxShadow: theme.palette.mode === 'dark'
      ? '0 4px 16px rgba(0,0,0,0.4)'
      : '0 4px 16px rgba(0,0,0,0.12)',
  },
  '& code:not(pre code)': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(14, 165, 233, 0.12)' : 'rgba(14, 165, 233, 0.08)',
    color: theme.palette.mode === 'dark' ? '#38bdf8' : '#0284c7',
    padding: '0.18em 0.45em',
    borderRadius: '5px',
    fontSize: '0.86em',
    fontFamily: '"SF Mono", "Fira Code", "Fira Mono", Menlo, Consolas, monospace',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(14, 165, 233, 0.2)' : 'rgba(14, 165, 233, 0.18)'}`,
  },
  '& pre code': {
    fontFamily: '"SF Mono", "Fira Code", "Fira Mono", Menlo, Consolas, monospace',
    backgroundColor: 'transparent',
    border: 'none',
    padding: 0,
    color: 'inherit',
  },
  '& a': {
    color: theme.palette.mode === 'dark' ? '#38bdf8' : '#0284c7',
    textDecoration: 'none',
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(56, 189, 248, 0.35)' : 'rgba(2, 132, 199, 0.3)'}`,
    transition: 'all 0.15s ease',
    '&:hover': {
      color: theme.palette.mode === 'dark' ? '#7dd3fc' : '#0369a1',
      borderBottomColor: theme.palette.mode === 'dark' ? '#7dd3fc' : '#0369a1',
    },
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    margin: '1rem 0',
    fontSize: '0.9em',
    overflow: 'hidden',
    borderRadius: '10px',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
    boxShadow: theme.palette.mode === 'dark'
      ? 'none'
      : '0 1px 6px rgba(0,0,0,0.05)',
  },
  '& th': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(14, 165, 233, 0.1)' : 'rgba(14, 165, 233, 0.06)',
    fontWeight: 700,
    textAlign: 'left',
    padding: '10px 16px',
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
    color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#0f172a',
    fontSize: '0.88em',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  '& td': {
    padding: '10px 16px',
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}`,
  },
  '& tr:last-child td': {
    borderBottom: 'none',
  },
  '& tr:hover td': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(14, 165, 233, 0.04)',
  },
  '& hr': {
    border: 'none',
    height: '1px',
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(90deg, transparent, rgba(14, 165, 233, 0.3), transparent)'
      : 'linear-gradient(90deg, transparent, rgba(14, 165, 233, 0.25), transparent)',
    margin: '1.75rem 0',
  },
  '& img': {
    maxWidth: '100%',
    borderRadius: '10px',
    margin: '0.5rem 0',
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
  },
  '& input[type="checkbox"]': {
    marginRight: '0.5rem',
    accentColor: '#0ea5e9',
  },
  '& strong': {
    fontWeight: 700,
    color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#0f172a',
  },
}));

const Preview = React.forwardRef(({ value }, ref) => {
  return (
    <PreviewContainer ref={ref} elevation={0}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        skipHtml={false}
        breaks={true}
      >
        {value}
      </ReactMarkdown>
    </PreviewContainer>
  );
});

export default Preview;
